import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { titleCaseName } from "@/lib/utils";
import { PhoneInput } from "@/components/PhoneInput";
import { toast } from "sonner";
import { Loader2, BadgeCheck, FileText, Flame, Mail, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

// Indicatif par défaut selon la langue de l'interface (modifiable librement par l'utilisateur).
const LANG_DEFAULT = {
  fr: ["FR", "+33"], en: ["GB", "+44"], es: ["ES", "+34"], es_mx: ["MX", "+52"],
  de: ["DE", "+49"], it: ["IT", "+39"], pl: ["PL", "+48"], fil: ["PH", "+63"],
  ru: ["RU", "+7"], el: ["GR", "+30"], hi: ["IN", "+91"], ja: ["JP", "+81"], zh: ["CN", "+86"],
};
const defaultDialFor = (lang) => {
  const k = (lang || "fr").toLowerCase().replace("-", "_");
  return LANG_DEFAULT[k] || LANG_DEFAULT[k.slice(0, 2)] || ["FR", "+33"];
};

export const AuthDialog = ({ open, onOpenChange, onSuccess, initialMode = "login" }) => {
  const { login, register } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);

  const routeAfterAuth = (u) => {
    if (u?.is_ambassador && (u.free_eternal_candles || 0) > 0) {
      onOpenChange(false);
      navigate("/ambassadeur");
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (open) { setMode(initialMode); setRPhoneTouched(false); }
  }, [open, initialMode]);
  // login
  const [lEmail, setLEmail] = useState("");
  const [lPass, setLPass] = useState("");
  // register
  const [rFirst, setRFirst] = useState("");
  const [rLast, setRLast] = useState("");
  const [rEmail, setREmail] = useState("");
  const [rPhone, setRPhone] = useState("");
  const [rCountry, setRCountry] = useState(() => defaultDialFor(i18n.language)[0]);
  const [rDial, setRDial] = useState(() => defaultDialFor(i18n.language)[1]);
  const [rPhoneTouched, setRPhoneTouched] = useState(false);
  const [rPass, setRPass] = useState("");
  const [terms, setTerms] = useState(false);
  const [notif, setNotif] = useState(true);

  // Pré-remplit l'indicatif selon la langue tant que l'utilisateur n'a pas saisi de numéro.
  useEffect(() => {
    if (rPhoneTouched) return;
    const [cc, dial] = defaultDialFor(i18n.language);
    setRCountry(cc);
    setRDial(dial);
  }, [i18n.language, rPhoneTouched]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(lEmail, lPass);
      toast.success(t("auth.welcome"));
      if (routeAfterAuth(u)) return;
      onOpenChange(false);
      onSuccess && onSuccess("login");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    if (!terms) return toast.error(t("auth.mustAcceptTerms"));
    setLoading(true);
    try {
      const u = await register({
        email: rEmail, name: `${rFirst} ${rLast}`.trim(), first_name: rFirst, last_name: rLast, password: rPass,
        phone: rPhone || null, country: rCountry, dial_code: rDial,
        accept_terms: terms, notifications_enabled: notif,
        language: (i18n.language || "fr").slice(0, 2),
      });
      toast.success(t("auth.accountCreated"));
      if (routeAfterAuth(u)) return;
      onOpenChange(false);
      onSuccess && onSuccess("register");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth.registerError"));
    } finally {
      setLoading(false);
    }
  };

  // iOS/iPad : quand le clavier s'ouvre, remonte le champ actif au-dessus du clavier.
  const keepFocusVisible = (e) => {
    const el = e.target;
    if (!el || typeof el.scrollIntoView !== "function") return;
    setTimeout(() => {
      try { el.style.scrollMarginTop = "5.5rem"; el.scrollIntoView({ block: "start", behavior: "smooth" }); } catch (_) { /* noop */ }
    }, 280);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} onFocusCapture={keepFocusVisible} className="glass max-h-[92vh] overflow-x-hidden overflow-y-auto rounded-3xl border-white/10 bg-[#141110]/95 p-0 sm:max-w-md !top-4 !translate-y-0 sm:!top-[50%] sm:!-translate-y-1/2" data-testid="auth-dialog">
        {/* Lueur de bougie décorative */}
        <div className="pointer-events-none absolute left-1/2 top-0 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-400/25 blur-3xl" aria-hidden="true" />
        <div className="relative px-6 pb-6 pt-7">
        {mode === "register" ? (
          <>
            <div className="mb-4 flex flex-col items-center text-center">
              <div className="relative mb-3 flex h-14 w-14 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-sky-400/30 blur-lg" />
                <div className="absolute inset-1 rounded-full bg-sky-300/25 blur-sm candle-pulse" />
                <Flame size={26} className="relative text-sky-300 drop-shadow-[0_0_10px_rgba(56,189,248,0.9)]" />
              </div>
              <DialogTitle className="text-2xl font-medium tracking-tight">{t("auth.regTitle")}</DialogTitle>
              <DialogDescription className="mt-1 text-sm font-light text-muted-foreground">{t("auth.regSubtitle")}</DialogDescription>
            </div>

            <div className="mx-auto mb-3 flex w-fit items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-medium text-emerald-300" data-testid="free-badge">
              <BadgeCheck size={14} /> {t("auth.freeBadge")}
            </div>

            <form onSubmit={doRegister} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="r-first">{t("auth.firstName")}</Label>
                  <Input id="r-first" required value={rFirst} onChange={(e) => setRFirst(titleCaseName(e.target.value))} className="mt-1 h-11 rounded-xl border-white/10 bg-white/[0.04]" data-testid="register-first-name" />
                </div>
                <div>
                  <Label htmlFor="r-last">{t("auth.lastName")}</Label>
                  <Input id="r-last" required value={rLast} onChange={(e) => setRLast(titleCaseName(e.target.value))} className="mt-1 h-11 rounded-xl border-white/10 bg-white/[0.04]" data-testid="register-last-name" />
                </div>
              </div>
              <div>
                <Label htmlFor="r-email">{t("auth.email")}</Label>
                <div className="relative mt-1">
                  <Mail size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="r-email" type="email" required value={rEmail} onChange={(e) => setREmail(e.target.value)} className="h-11 rounded-xl border-white/10 bg-white/[0.04] pl-10" data-testid="register-email" />
                </div>
              </div>
              <div>
                <Label>{t("auth.phone")}</Label>
                <PhoneInput code={rCountry} phone={rPhone ? `${rDial} ${rPhone}` : ""} onChange={({ code, dial, number }) => { setRCountry(code); setRDial(dial); setRPhone(number); setRPhoneTouched(true); }} testid="register" />
              </div>
              <div>
                <Label htmlFor="r-pass">{t("auth.password")}</Label>
                <div className="relative mt-1">
                  <Lock size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-muted-foreground" />
                  <PasswordInput id="r-pass" required minLength={6} value={rPass} onChange={(e) => setRPass(e.target.value)} className="h-11 rounded-xl border-white/10 bg-white/[0.04] pl-10" data-testid="register-password" />
                </div>
              </div>

              <label className="flex items-start gap-2 pt-1 text-sm text-foreground/90">
                <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5" data-testid="register-terms-checkbox" />
                <span>{t("auth.termsAccept")}</span>
              </label>
              <a href="/conditions" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline" data-testid="read-terms-link">
                <FileText size={12} /> {t("auth.readTerms")}
              </a>

              <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-card/40 px-4 py-2.5">
                <span className="text-sm">{t("auth.enableNotifications")}</span>
                <Switch checked={notif} onCheckedChange={setNotif} data-testid="register-notifications-switch" />
              </div>

              <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold shadow-[0_4px_18px_0_rgba(56,189,248,0.4)] transition-transform hover:scale-[1.01]" disabled={loading || !terms} data-testid="register-submit">
                {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.createAccount")}
              </Button>
            </form>

            <button type="button" onClick={() => setMode("login")} className="mt-3 block w-full text-center text-sm font-medium text-sky-400 hover:text-sky-300 hover:underline" data-testid="switch-to-login">
              {t("auth.alreadyMember")}
            </button>
          </>
        ) : (
          <>
            <div className="mb-5 flex flex-col items-center text-center">
              <div className="relative mb-3 flex h-16 w-16 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-sky-400/30 blur-xl" />
                <div className="absolute inset-1.5 rounded-full bg-sky-300/25 blur-md candle-pulse" />
                <Flame size={30} className="relative text-sky-300 drop-shadow-[0_0_12px_rgba(56,189,248,0.95)]" />
              </div>
              <DialogTitle className="text-2xl font-medium tracking-tight">{t("auth.login")}</DialogTitle>
              <DialogDescription className="mt-1 text-sm font-light text-muted-foreground">{t("auth.desc")}</DialogDescription>
            </div>

            <form onSubmit={doLogin} className="space-y-4">
              <div>
                <Label htmlFor="l-email" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{t("auth.email")}</Label>
                <div className="relative mt-1.5">
                  <Mail size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="l-email" type="email" required value={lEmail} onChange={(e) => setLEmail(e.target.value)} className="h-12 rounded-xl border-white/10 bg-white/[0.04] pl-10 focus-visible:ring-sky-400/50" data-testid="login-email" />
                </div>
              </div>
              <div>
                <Label htmlFor="l-pass" className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{t("auth.password")}</Label>
                <div className="relative mt-1.5">
                  <Lock size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 z-10 text-muted-foreground" />
                  <PasswordInput id="l-pass" required value={lPass} onChange={(e) => setLPass(e.target.value)} className="h-12 rounded-xl border-white/10 bg-white/[0.04] pl-10 focus-visible:ring-sky-400/50" data-testid="login-password" />
                </div>
              </div>
              <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold shadow-[0_4px_18px_0_rgba(56,189,248,0.45)] transition-transform hover:scale-[1.01]" disabled={loading} data-testid="login-submit">
                {loading ? <Loader2 className="animate-spin" size={18} /> : t("auth.signIn")}
              </Button>
            </form>

            <div className="my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-xs text-muted-foreground">{t("auth.noAccount")}</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <Button variant="outline" onClick={() => setMode("register")} className="h-12 w-full rounded-full border-sky-400/30 bg-sky-400/5 text-base font-medium text-sky-200 transition-colors hover:bg-sky-400/10" data-testid="switch-to-register">
              {t("auth.createAccount")}
            </Button>
          </>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
